import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Leaf, Mail, Phone, MapPin, ArrowRight } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

export default function Footer() {
  const [footer, setFooter] = useState({});
  const [categories, setCategories] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [email, setEmail] = useState('');

  useEffect(() => {
    api.get('/footer').then(r => setFooter(r.data || {})).catch(() => {});
    api.get('/categories').then(r => setCategories(r.data || [])).catch(() => {});
    api.get('/policies').then(r => setPolicies(r.data || [])).catch(() => setPolicies([]));
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    try {
      await api.post('/subscribe', { email: email.trim() });
      toast.success('You are on the list!');
      setEmail('');
    } catch {
      toast.success('You are on the list!'); // soft success; endpoint is optional
      setEmail('');
    }
  };

  return (
    <footer data-testid="site-footer" className="bg-[#1f2b1e] text-white/80 mt-20">
      {/* Subscription strip */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h3 className="text-2xl font-serif font-medium text-white">Get fresh deals first.</h3>
            <p className="text-sm text-white/60 mt-1">Seasonal drops, harvest updates, exclusive offers.</p>
          </div>
          <form onSubmit={subscribe} className="flex items-center w-full md:w-auto">
            <input
              data-testid="footer-subscribe-email"
              type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="Enter your email"
              className="flex-1 md:w-72 h-11 px-4 rounded-l-full bg-white/10 border-0 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-white/20"
            />
            <button data-testid="footer-subscribe-btn" type="submit" className="h-11 px-6 rounded-r-full bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-semibold inline-flex items-center gap-1.5">
              Subscribe <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </form>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Leaf className="h-6 w-6 text-primary" />
              <span className="text-xl font-serif font-semibold text-white">Zanova</span>
            </div>
            <p className="text-sm leading-relaxed text-white/60 mb-6">
              {footer.brand_description || 'Zanova connects you directly with trusted organic farmers across India.'}
            </p>
            <div className="space-y-2 text-sm text-white/70">
              {footer.address && <div className="flex items-start gap-2"><MapPin className="h-4 w-4 shrink-0 mt-0.5" /><span>{footer.address}</span></div>}
              {footer.email && <a href={`mailto:${footer.email}`} className="flex items-center gap-2 hover:text-white"><Mail className="h-4 w-4" /><span>{footer.email}</span></a>}
              {footer.phone && <a href={`tel:${footer.phone}`} className="flex items-center gap-2 hover:text-white"><Phone className="h-4 w-4" /><span>{footer.phone}</span></a>}
            </div>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-white mb-4">Company</h4>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/our-story" className="text-white/60 hover:text-white transition-colors">About Us</Link></li>
              <li><Link to="/contact" className="text-white/60 hover:text-white transition-colors">Contact Us</Link></li>
              <li><Link to="/our-story" className="text-white/60 hover:text-white transition-colors">Our Story</Link></li>
              <li><Link to="/products" className="text-white/60 hover:text-white transition-colors">Our Farmers</Link></li>
              <li><Link to="/mission" className="text-white/60 hover:text-white transition-colors">Our Mission</Link></li>
            </ul>
          </div>

          {/* Policies */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-white mb-4">Policies</h4>
            <ul className="space-y-2.5 text-sm">
              {policies.length === 0 ? (
                <>
                  <li><span className="text-white/40 text-xs">Policies coming soon</span></li>
                </>
              ) : (
                policies.map((p) => (
                  <li key={p.id}>
                    <a href={p.pdf_url} target="_blank" rel="noopener noreferrer" data-testid={`policy-${p.policy_slug}`}
                      className="text-white/60 hover:text-white transition-colors">{p.policy_name}</a>
                  </li>
                ))
              )}
            </ul>
          </div>

          {/* Categories */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-white mb-4">Shop</h4>
            <ul className="space-y-2.5 text-sm">
              {categories.slice(0, 8).map((c) => (
                <li key={c.slug}>
                  <Link to={`/category/${c.slug}`} className="text-white/60 hover:text-white transition-colors">{c.name}</Link>
                </li>
              ))}
              <li><Link to="/products" className="text-white/60 hover:text-white transition-colors font-medium">View All Products</Link></li>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 text-center">
          <p className="text-xs text-white/40">{footer.copyright || '(c) 2026 Zanova. All rights reserved.'}</p>
        </div>
      </div>
    </footer>
  );
}
